/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React, { Component } from 'react';
import PropTypes from "prop-types";
import { EuiI18n } from '../../i18n';
import { EuiFlexGroup, EuiFlexItem } from '../../flex';
import { EuiSelect, EuiFieldNumber, EuiFormLabel, EuiSwitch } from '../../form';
import { htmlIdGenerator } from '../../../services';
import { EuiScreenReaderOnly } from '../../accessibility';
import { RenderI18nTimeOptions } from '../super_date_picker/time_options';
var MILLISECONDS_IN_SECOND = 1000;
var MILLISECONDS_IN_MINUTE = MILLISECONDS_IN_SECOND * 60;
var MILLISECONDS_IN_HOUR = MILLISECONDS_IN_MINUTE * 60;

function fromMilliseconds(milliseconds) {
  var round = function round(value) {
    return parseFloat(value.toFixed(2));
  };

  if (milliseconds > MILLISECONDS_IN_HOUR) {
    return {
      units: 'h',
      value: round(milliseconds / MILLISECONDS_IN_HOUR)
    };
  }

  if (milliseconds > MILLISECONDS_IN_MINUTE) {
    return {
      units: 'm',
      value: round(milliseconds / MILLISECONDS_IN_MINUTE)
    };
  }

  return {
    units: 's',
    value: round(milliseconds / MILLISECONDS_IN_SECOND)
  };
}

function toMilliseconds(units, value) {
  switch (units) {
    case 'h':
      return Math.round(value * MILLISECONDS_IN_HOUR);

    case 'm':
      return Math.round(value * MILLISECONDS_IN_MINUTE);

    case 's':
    default:
      return Math.round(value * MILLISECONDS_IN_SECOND);
  }
}

export class EuiRefreshInterval extends Component {
  static defaultProps = {
    isPaused: true,
    refreshInterval: 1000
  };
  state = fromMilliseconds(this.props.refreshInterval || 0);
  generateId = htmlIdGenerator();
  legendId = this.generateId();
  refreshSelectionId = this.generateId();
  onValueChange = event => {
    const sanitizedValue = parseFloat(event.target.value);
    this.setState({
      value: isNaN(sanitizedValue) ? '' : sanitizedValue
    }, this.applyRefreshInterval);
  };
  onUnitsChange = event => {
    this.setState({
      units: event.target.value
    }, this.applyRefreshInterval);
  };
  startRefresh = () => {
    const {
      onRefreshChange
    } = this.props;
    const {
      value,
      units
    } = this.state;

    if (value !== '' && value > 0 && onRefreshChange !== undefined) {
      onRefreshChange({
        refreshInterval: toMilliseconds(units, value),
        isPaused: false
      });
    }
  };
  handleKeyDown = ({
    key
  }) => {
    if (key === 'Enter') {
      this.startRefresh();
    }
  };
  applyRefreshInterval = () => {
    const {
      onRefreshChange,
      isPaused
    } = this.props;
    const {
      units,
      value
    } = this.state;

    if (value === '') {
      return;
    }

    if (!onRefreshChange) {
      return;
    }

    const refreshInterval = toMilliseconds(units, value);
    onRefreshChange({
      refreshInterval,
      isPaused: refreshInterval <= 0 ? true : !!isPaused
    });
  };
  toggleRefresh = () => {
    const {
      onRefreshChange,
      isPaused
    } = this.props;
    const {
      units,
      value
    } = this.state;

    if (!onRefreshChange || value === '') {
      return;
    }

    onRefreshChange({
      refreshInterval: toMilliseconds(units, value),
      isPaused: !isPaused
    });
  };
  renderScreenReaderText = refreshUnitsOptions => {
    const {
      isPaused
    } = this.props;
    const {
      value,
      units
    } = this.state;
    const options = refreshUnitsOptions.find(({
      value
    }) => value === units);
    const optionText = options ? options.text : '';
    const fullDescription = isPaused ? <EuiI18n token="euiRefreshInterval.fullDescriptionOff" default="Refresh is off, interval set to {optionValue} {optionText}." values={{
      optionValue: value,
      optionText
    }} /> : <EuiI18n token="euiRefreshInterval.fullDescriptionOn" default="Refresh is on, interval set to {optionValue} {optionText}." values={{
      optionValue: value,
      optionText
    }} />;
    return <EuiScreenReaderOnly>
        <p id={this.refreshSelectionId}>{fullDescription}</p>
      </EuiScreenReaderOnly>;
  };

  render() {
    const {
      isPaused
    } = this.props;
    const {
      value,
      units
    } = this.state;
    return <RenderI18nTimeOptions>
        {({
        refreshUnitsOptions
      }) => <fieldset>
            <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false} wrap>
              <EuiFlexItem grow={false}>
                <EuiSwitch data-test-subj="superDatePickerToggleRefreshButton" aria-describedby={this.refreshSelectionId} checked={!isPaused} onChange={this.toggleRefresh} compressed label={<EuiFormLabel type="legend" id={this.legendId}>
                      <EuiI18n token="euiRefreshInterval.legend" default="Refresh every" />
                    </EuiFormLabel>} />
              </EuiFlexItem>
              <EuiFlexItem style={{
            minWidth: 60
          }}>
                <EuiFieldNumber compressed fullWidth value={value} onChange={this.onValueChange} onKeyDown={this.handleKeyDown} isInvalid={!isPaused && (value === '' || value <= 0)} disabled={isPaused} aria-label="Refresh interval value" aria-describedby={`${this.refreshSelectionId} ${this.legendId}`} data-test-subj="superDatePickerRefreshIntervalInput" />
              </EuiFlexItem>
              <EuiFlexItem style={{
            minWidth: 100
          }} grow={2}>
                <EuiSelect compressed fullWidth aria-label="Refresh interval units" aria-describedby={`${this.refreshSelectionId} ${this.legendId}`} value={units} disabled={isPaused} options={refreshUnitsOptions} onChange={this.onUnitsChange} onKeyDown={this.handleKeyDown} data-test-subj="superDatePickerRefreshIntervalUnitsSelect" />
              </EuiFlexItem>
            </EuiFlexGroup>
            {this.renderScreenReaderText(refreshUnitsOptions)}
          </fieldset>}
      </RenderI18nTimeOptions>;
  }

}
EuiRefreshInterval.propTypes = {
  /**
     * Is refresh paused or running.
     */
  isPaused: PropTypes.bool,

  /**
     * Refresh interval in milliseconds.
     */
  refreshInterval: PropTypes.number,

  /**
     * Passes back the updated state of `isPaused` and `refreshInterval`.
     */
  onRefreshChange: PropTypes.func.isRequired
};

try {
  EuiRefreshInterval.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/date_picker/auto_refresh/refresh_interval.tsx',
    description: '',
    displayName: 'EuiRefreshInterval',
    methods: [],
    props: {
      isPaused: {
        defaultValue: {
          value: 'true'
        },
        description: 'Is refresh paused or running.',
        name: 'isPaused',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/auto_refresh/refresh_interval.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      refreshInterval: {
        defaultValue: {
          value: '1000'
        },
        description: 'Refresh interval in milliseconds.',
        name: 'refreshInterval',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/auto_refresh/refresh_interval.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      onRefreshChange: {
        defaultValue: null,
        description: 'Passes back the updated state of `isPaused` and `refreshInterval`.',
        name: 'onRefreshChange',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/auto_refresh/refresh_interval.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'ApplyRefreshInterval'
        }
      }
    },
    extendedInterfaces: []
  };
} catch (e) {}